/**
 * Network Status Context - Provides global network status state
 * Following SOLID principles with clear separation of concerns
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { networkService, NetworkConnectionType } from "../services/networkService";

// Create context
const NetworkStatusContext = createContext();

/**
 * Network Status Provider Component
 * Manages global network status state and provides it to children
 */
export const NetworkStatusProvider = ({ children, config = {} }) => {
  const [networkStatus, setNetworkStatus] = useState({
    isOnline: navigator.onLine,
    speed: null,
    connectionType: NetworkConnectionType.GOOD,
    latency: null,
    lastChecked: Date.now(),
    isLoading: true
  });

  // Memoized status update handler
  const handleStatusUpdate = useCallback((status) => {
    setNetworkStatus(prevStatus => ({
      ...prevStatus,
      ...status,
      isLoading: false
    }));
  }, []);

  // Handle config changes separately to avoid infinite loop
  useEffect(() => {
    if (config?.pingInterval) {
      networkService.pingInterval = config.pingInterval;
    }
    if (config?.speedTestInterval) {
      networkService.speedTestInterval = config.speedTestInterval;
    }
  }, [config?.pingInterval, config?.speedTestInterval]);

  // Initialize network service and subscribe to updates (only once)
  useEffect(() => {
    // Subscribe to network status updates
    const unsubscribe = networkService.subscribe(handleStatusUpdate);

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [handleStatusUpdate]);

  // Manually trigger network check
  const checkConnection = useCallback(async () => {
    setNetworkStatus(prev => ({ ...prev, isLoading: true }));
    await networkService.checkConnection();
  }, []);

  // Manually trigger speed test
  const checkSpeed = useCallback(async () => {
    if (networkStatus.isOnline) {
      await networkService.measureNetworkSpeed();
    }
  }, [networkStatus.isOnline]);

  // Get connection quality description
  const getConnectionQuality = useCallback(() => {
    switch (networkStatus.connectionType) {
    case NetworkConnectionType.GOOD:
      return {
        label: "Good",
        description: "Stable & Fast Connection",
        color: "#52c41a", // Green
        icon: "🟢"
      };
    case NetworkConnectionType.POOR:
      return {
        label: "Poor",
        description: "Slow or Unstable Connection",
        color: "#faad14", // Yellow
        icon: "🟡"
      };
    case NetworkConnectionType.OFFLINE:
      return {
        label: "Offline",
        description: "No Internet Connection",
        color: "#ff4d4f", // Red
        icon: "🔴"
      };
    default:
      return {
        label: "Unknown",
        description: "Connection status unknown",
        color: "#d9d9d9", // Gray
        icon: "⚪"
      };
    }
  }, [networkStatus.connectionType]);

  // Format speed for display
  const getFormattedSpeed = useCallback(() => {
    if (networkStatus.speed === null) return "Unknown";
    if (networkStatus.speed < 1) {
      return `${Math.round(networkStatus.speed * 1000)} Kbps`;
    }
    return `${networkStatus.speed.toFixed(1)} Mbps`;
  }, [networkStatus.speed]);

  // Format latency for display
  const getFormattedLatency = useCallback(() => {
    if (networkStatus.latency === null) return "Unknown";
    return `${networkStatus.latency}ms`;
  }, [networkStatus.latency]);

  // Context value
  const contextValue = {
    // Status data
    ...networkStatus,
    
    // Helper methods
    checkConnection,
    checkSpeed,
    getConnectionQuality,
    getFormattedSpeed,
    getFormattedLatency,
    
    // Raw service access (for advanced use cases)
    networkService
  };

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      {children}
    </NetworkStatusContext.Provider>
  );
};

NetworkStatusProvider.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.shape({
    pingInterval: PropTypes.number,
    speedTestInterval: PropTypes.number,
    debounceDelay: PropTypes.number
  })
};

/**
 * Custom hook to use network status
 * Provides easy access to network status and helper methods
 */
export const useNetworkStatus = () => {
  const context = useContext(NetworkStatusContext);
  
  if (context === undefined) {
    throw new Error("useNetworkStatus must be used within a NetworkStatusProvider");
  }
  
  return context;
};

/**
 * Higher-order component that injects network status props
 * Useful for class components or components that need network status
 */
export const withNetworkStatus = (WrappedComponent) => {
  const WithNetworkStatusComponent = (props) => {
    const networkStatus = useNetworkStatus();
    
    return (
      <WrappedComponent
        {...props}
        networkStatus={networkStatus}
      />
    );
  };
  
  WithNetworkStatusComponent.displayName = 
    `withNetworkStatus(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithNetworkStatusComponent;
};

/**
 * Hook for components that only need to know if online/offline
 * Lighter alternative for simple use cases
 */
export const useOnlineStatus = () => {
  const { isOnline, connectionType } = useNetworkStatus();
  
  return {
    isOnline,
    isOffline: !isOnline,
    connectionType
  };
};

/**
 * Hook for components that need connection quality info
 * Returns formatted quality information
 */
export const useConnectionQuality = () => {
  const { getConnectionQuality, connectionType, speed, latency } = useNetworkStatus();
  
  return {
    ...getConnectionQuality(),
    connectionType,
    speed,
    latency
  };
};

export default NetworkStatusContext;
